import Box from '@mui/material/Box';
import { get } from 'lodash';
import { useEffect } from 'react';
import { usePermissions, useStore, useTranslate } from 'react-admin';
import { Outlet, useParams } from 'react-router';
import useGetAvatar from '@/hooks/useGetAvatar';
import { composeUri } from '@/utils/compose';
import {
    AVATARS,
    BIG_ADMIN,
    BIG_AGENCY_MANAGER,
    BIG_AGENCY_OPERATOR,
    BIG_OPERATOR,
    BIG_SUPER_ADMIN,
    CURRENT_CLIENT_NAVIGATION_STORE_KEY,
    DOE_DOCUMENT_TAG,
    FICHE_MISE_SERVICE_TAG,
    FICHE_RENSEIGNEMENTS_DOCUMENT_TAG,
    INITIAL_AUDIT_TAG,
    PUBLIC_DOCUMENTS_URL_DOWNLOAD,
    SERVICE_CONDITIONS_TAG,
} from '@/utils/CONST';
import CustomBlock from '@/components/common/custom-block/custom-block';
import DashboardsInfoBox from '@/components/common/dashboards-info-box/dashboards-info-box';
import DocumentsBox from '@/components/common/documents-box/documents-box';

const UPLOAD_ALLOWED_PROFILES = [
    BIG_SUPER_ADMIN,
    BIG_ADMIN,
    BIG_OPERATOR,
    BIG_AGENCY_MANAGER,
    BIG_AGENCY_OPERATOR,
];

const PlaceInformation = () => {
    const translate = useTranslate();
    const { permissions } = usePermissions();
    const { placeId } = useParams();
    const canUploadDocument = permissions?.some((permission: string) =>
        UPLOAD_ALLOWED_PROFILES.includes(permission),
    );
    const { avatar, isLoading } = useGetAvatar({
        avatarUriId: composeUri(AVATARS, placeId),
        filters: {
            'serialization_groups[0]': 'read_avatar_root',
            'serialization_groups[1]': 'read_parent',
            'serialization_groups[2]': 'read_avatar_parent',
        },
    });
    const [, setCurrentClientNavigation]: any = useStore(CURRENT_CLIENT_NAVIGATION_STORE_KEY, undefined);

    useEffect(() => {
        if (avatar) {
            setCurrentClientNavigation(get(avatar, 'parent.parent.@id'));
        }
    }, [avatar]);

    return (
        <Box
            data-cy='place-information-container'
            sx={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: 1,
                gridTemplateRows: '1fr',
                height: '100%',
                overflowY: 'auto',
            }}
        >
            <CustomBlock label={avatar?.name} loading={isLoading}>
                <Box
                    sx={{
                        p: 1,
                        display: 'grid',
                        gap: 3,
                        alignContent: 'start',
                        justifyContent: 'center',
                    }}
                >
                    {avatar && (
                        <>
                            <DashboardsInfoBox
                                avatar={avatar}
                                params={{
                                    place_id: placeId,
                                }}
                                clientOrSite='place'
                            />

                            <DocumentsBox
                                withUploadButton={canUploadDocument}
                                items={[
                                    {
                                        label: translate('place.documentation.commissioning_sheet'),
                                        tag: FICHE_MISE_SERVICE_TAG,
                                    },
                                    {
                                        label: translate('place.documentation.information_sheet'),
                                        tag: FICHE_RENSEIGNEMENTS_DOCUMENT_TAG,
                                    },
                                    {
                                        label: translate('place.documentation.doe'),
                                        tag: DOE_DOCUMENT_TAG,
                                    },
                                    {
                                        label: translate('place.documentation.dtu'),
                                        subLabel: translate('place.documentation.annexe_e'),
                                        fixedUrlDownload: `${PUBLIC_DOCUMENTS_URL_DOWNLOAD}/DTU13-3%20-2022%20-%20Annexe%20E.pdf`,
                                    },
                                    {
                                        label: translate('place.documentation.documentation_aqc'),
                                        fixedUrlDownload: `${PUBLIC_DOCUMENTS_URL_DOWNLOAD}/AQC-Dallages-Industriels.pdf`,
                                    },
                                    {
                                        label: translate('place.documentation.audit'),
                                        tag: INITIAL_AUDIT_TAG,
                                    },
                                    {
                                        label: translate('place.documentation.service_conditions'),
                                        tag: SERVICE_CONDITIONS_TAG,
                                    },
                                ]}
                            />
                        </>
                    )}
                </Box>
            </CustomBlock>

            <Outlet />
        </Box>
    );
};

export default PlaceInformation;
